import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  IconButton,
  Typography,
  Box,
  Divider,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Close as CloseIcon,
  Google as GoogleIcon,
} from '@mui/icons-material';
import axios from 'axios';

interface RegisterDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (user: any) => void;
  onSwitchToLogin: () => void;
  translations: any;
  handleGoogleButtonClick: () => void;
}

export const RegisterDialog = ({
  open,
  onClose,
  onSuccess,
  onSwitchToLogin,
  translations: t,
  handleGoogleButtonClick,
}: RegisterDialogProps) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [registerForm, setRegisterForm] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    signup_source: 'website',
  });

  const handleRegisterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (registerForm.password !== registerForm.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const { data } = await axios.post('/api/register', registerForm);
      
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      onSuccess(data.user);
      
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleRegisterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRegisterForm({
      ...registerForm,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
     <DialogTitle sx={{ 
          textAlign: 'center',
          fontFamily: 'Merriweather, serif',
          pb: 1,
        }}>
          {t.createAnAccount}
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <form onSubmit={handleRegisterSubmit}>
          <DialogContent sx={{ pt: 1 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            <Button
              fullWidth
              variant="outlined"
              startIcon={<GoogleIcon />}
              onClick={handleGoogleButtonClick}
              sx={{ mb: 2 }}
            >
              {t.signupWithGoogle}
            </Button>
            <Divider sx={{ my: 2 }}>or</Divider>
            <TextField
              autoFocus
              margin="dense"
              name="name"
              label={t.name}
              type="text"
              fullWidth
              variant="outlined"
              value={registerForm.name}
              onChange={handleRegisterChange}
              required
              disabled={loading}
            />
            <TextField
              margin="dense"
              name="email"
              label={t.email}
              type="email"
              fullWidth
              variant="outlined"
              value={registerForm.email}
              onChange={handleRegisterChange}
              required
              disabled={loading}
            />
            <TextField
              margin="dense"
              name="password"
              label={t.password}
              type="password"
              fullWidth
              variant="outlined"
              value={registerForm.password}
              onChange={handleRegisterChange}
              required
              disabled={loading}
            />
            <TextField
              margin="dense"
              name="confirmPassword"
              label={t.confirmPassword}
              type="password"
              fullWidth
              variant="outlined"
              value={registerForm.confirmPassword}
              onChange={handleRegisterChange}
              required
              disabled={loading}
            />
          </DialogContent>
          <DialogActions sx={{ flexDirection: 'column', p: 3, pt: 1 }}>
            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={loading}
              sx={{
                backgroundColor: '#382110',
                color: '#FFFFFF',
                '&:hover': {
                  backgroundColor: '#58371F',
                },
              }}
            >
              {loading ? <CircularProgress size={24} /> : t.createAnAccount}
            </Button>
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                {t.alreadyHaveAnAccount} {' '}
                <Button
                  onClick={() => {
                    onClose();
                    onSwitchToLogin();
                  }}
                  sx={{ 
                    textTransform: 'none',
                    fontWeight: 'normal',
                    p: 0,
                    ml: 0.5,
                  }}
                >
                  {t.signin}
                </Button>
              </Typography>
            </Box>
          </DialogActions>
        </form>
    </Dialog>
  );
}; 